<template>
  <MuiForm
    v-model="formData"
    class="SignUpEmployeeStep4 Step__form"
    @submit.native="handlerFormSubmit"
  >
    <h1
      class="font-normal m-xs-b"
    >
      {{ $t('Views.user_signup.step_4.title') }}
    </h1>
    <div
      class="m-s-b"
    >
      {{ $t('Views.user_signup.step_4.subtitle') }}
    </div>
    <div class="d-flex no-gutters">
      <div class="col">
        <!-- Location -->
        <MuiInputLocation
          :label="$t('Views.user_signup.step_4.fields.address')"
          :placeholder="getAddressHint"
          :value="data.address"
          :google="google"
          :on-blur="() => $segments.trackSegment('blur', { address: data.address.address })"
          name="address"
          required
          full-width
          @change="(v) => address(v)"
        />
      </div>
      <template v-if="false">
        <!-- TODO: It doesn't work in version 0.0.108 of motion -->
        <div class="Step__form__only-desktop d-flex col-flex m-xs-left align-items-center">
          <MuiTooltip
            :arrow="false"
            :text="$t('Views.user_signup.step_4.fields.address_tooltip')"
            horizontal-position="right"
            name="infoIcon"
            vertical-position="middle"
          >
            <MuiInfoIcon size="24" />
          </MuiTooltip>
        </div>
      </template>
    </div>
    <div class="Step__form__small-width">
      <!-- Additional Location -->
      <MuiInputText
        :label="$t('Views.user_signup.step_4.fields.additional_address')"
        :placeholder="getAdditionalAddressHint"
        :value="data.addressDetails"
        full-width
        name="additional_address"
        @change="(v) => addressDetails(v)"
      />
      <!-- Phone -->
      <MuiInputPhone
        :country-label="$t('Views.user_signup.step_4.fields.phone_prefix')"
        :label="$t('Views.user_signup.step_4.fields.phone_number')"
        :default-country="data.country ? data.country : 'es'"
        :value="data.phone"
        :on-blur="() => $segments.trackSegment('Phone Number Added', { phone: data.phone })"
        name="phone"
        required
        full-width
        @change="(v) => phone(v)"
      />
      <!-- Promo & Transition -->
    </div>
    <div class="divider m-m-t m-m-b" />
    <!-- Legal Documents -->
    <LegalDocuments
      v-if="info.legalDocuments"
      :documents="info.legalDocuments"
      name="legalforms"
      operator-lang="Views.user_signup.step_4.legal_documents.operator"
      general-lang="Views.user_signup.step_4.legal_documents.general"
    />
    <!-- Receive News -->
    <MuiCheckbox
      :label="$t('Views.user_signup.step_4.legal_documents.receive_news')"
      :value="data.receiveNews"
      name="receiveNews"
      size="xs"
      label-position="right"
      @change="(v) => receiveNews(v)"
    />
    <MuiButtonFlat
      :is-loading="STATUS.LOADING"
      :disabled="STATUS.LOADING"
      submit
      class="Step__form__next-button"
    >
      {{ $t('Views.user_signup.step_4.next') }}
    </MuiButtonFlat>
  </MuiForm>
</template>
<script>
import {
  mapMutations,
  mapState,
  mapActions,
} from 'vuex';
import { gmapApi as google } from 'vue2-google-maps';

import {
  MuiButtonFlat,
  MuiCheckbox,
  MuiForm,
  MuiInputLocation,
  MuiInputPhone,
  MuiInputText,
  MuiTooltip,
  ModalSizes,
} from '@ubeeqo/motion-ui';
import { LegalDocuments } from '@/domains/Auth/components';
import { Auth } from '@/main';
// Constants
import { EMPLOYEE_MODULE } from '@/constants/modules';

export default {
  name: 'SignUpEmployeeStep4View',
  components: {
    MuiForm,
    MuiInputLocation,
    MuiInputText,
    MuiInputPhone,
    MuiTooltip,
    LegalDocuments,
    MuiCheckbox,
    MuiButtonFlat,
  },
  data() {
    return {
      formData: {},
    };
  },
  computed: {
    ...mapState(EMPLOYEE_MODULE, {
      info: state => state.info,
      data: state => state.data,
      done: state => state.done,
      STATUS: state => state.STATUS,
      error: state => state.error,
    }),
    ...mapState('Setup', {
      api: state => state.apiInstance,
    }),
    google: () => google(),
    getAddressHint() {
      return this.$t(`Views.user_signup.step_4.hint.${this.data.country ? this.data.country : 'GB'}.address`);
    },
    getAdditionalAddressHint() {
      return this.$t(`Views.user_signup.step_4.hint.${this.data.country ? this.data.country : 'GB'}.additional_address`);
    },
  },
  watch: {
    formData(value) {
      if (value.valid) {
        this.doneStep(this.$options.name);
      } else {
        this.unDoneStep(this.$options.name);
      }
    },
  },
  mounted() {
    if (!this.done.step3) {
      this.$router.push('./step3');
    }
  },
  methods: {
    ...mapMutations(EMPLOYEE_MODULE, [
      'doneStep',
      'unDoneStep',
      'updateStep',
      'address',
      'addressDetails',
      'phone',
      'receiveNews',
      'resetUser',
    ]),
    ...mapActions(EMPLOYEE_MODULE, [
      'createEmployeeFromInvitation',
      'createUser',
    ]),
    handlerFormSubmit() {
      if (this.$data.formData.valid) {
        this.registerUser();
      }
    },
    async registerUser() {
      const token = this.$route.params.invitationToken;

      await (token
        ? this.createEmployeeFromInvitation(token)
        : this.createUser());

      if (this.STATUS.LOADED) {
        this.showSuccessModal();
      } else if (this.STATUS.ERROR) {
        this.$notifications.create({
          delayToClose: false,
          text: this.error.response ? this.error.response.data.message : this.error.data.message,
          textAction: 'Close',
          type: 'danger',
        });
      }
    },
    showSuccessModal() {
      this.$modal.show('SuccessModal', {
        size: ModalSizes.small,
        closeButton: false,
        cancelButton: {},
        title: 'Account created',
        newHeader: true,
        headerBgColor: 'secondary',
        footerAlign: 'right',
        content: {
          props: {
            text: this.$t('Views.user_signup.step_4.success.text'),
          },
        },
        acceptButton: {
          type: 'main',
          text: this.$t('Views.user_signup.step_4.success.action'),
          action: () => {
            Auth.logout();
            this.$modal.close('SuccessModal');
          },
        },
      });
    },
  },
};
</script>
